// src/components/crm/CustomerTaxExemptionSection.js — taxexempt3-8c4f2a1d (tax codes on every line, Stage S3)
//
// "Tax exemption" on the customer record (mockup panel 1): "This customer is exempt from some or all
// tax", the company's own tax parts to tick, and the exemption number (REQUIRED when a part is ticked —
// the server enforces it too). Saved on its own, straight to the server (setCustomerTaxExemption), never
// through the customer form's save, so the form can't overwrite it.
// Owner, Admin, Manager and Office Staff can change it; everyone else sees it read-only.
// Renders NOTHING unless exemptions are on for the company (lineTax.exemptionsSwitchOn).
import React, { useEffect, useMemo, useState } from 'react';
import {
    exemptionPartChoices, customerExemptionOf, exemptionPartsText, cleanExemptionNumber, normTaxPartName, EXEMPTION_NUMBER_MAX,
} from '../../utils/lineTax';
import { useTaxCodesDoc, exemptionsOnFor, canChangeTaxExemption, setCustomerTaxExemptionCall, callErrorText } from '../../utils/taxExemptionUi';

export default function CustomerTaxExemptionSection({ orgId, customer, companySettings, currentUserProfile, idPrefix = 'cte' }) {
    const taxCodesDoc = useTaxCodesDoc(orgId);
    const saved = customerExemptionOf(customer);
    const [exempt, setExempt] = useState(!!saved);
    const [ticked, setTicked] = useState(() => new Set((saved ? saved.parts : []).map(normTaxPartName)));
    const [number, setNumber] = useState(saved ? saved.number : '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const savedKey = saved ? `${saved.parts.join('|')}#${saved.number}` : '';
    useEffect(() => { // the customer doc changed (another tab, or our own save): show what is stored
        setExempt(!!saved); setTicked(new Set((saved ? saved.parts : []).map(normTaxPartName))); setNumber(saved ? saved.number : '');
    }, [savedKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const qbConnected = companySettings?.quickbooksConnected === true;
    const choices = useMemo(() => exemptionPartChoices(taxCodesDoc, qbConnected), [taxCodesDoc, qbConnected]);
    if (!customer?.id || !exemptionsOnFor(orgId, taxCodesDoc)) return null;
    const office = canChangeTaxExemption(currentUserProfile);

    if (!office) {
        return (
            <div className="border border-gray-200 rounded-xl p-4 bg-white text-sm" data-testid="customer-tax-exemption">
                <p className="font-bold text-gray-900 mb-1">Tax exemption</p>
                <p className="text-gray-700">{saved ? `Exempt from ${exemptionPartsText(saved.parts)} · #${saved.number}` : 'Not exempt'}</p>
                <p className="text-xs text-gray-500 mt-2">Owner, Admin, Manager and Office Staff can change this.</p>
            </div>
        );
    }

    const parts = exempt ? choices.filter((c) => ticked.has(normTaxPartName(c.name))).map((c) => c.name) : [];
    const numberOk = !!cleanExemptionNumber(number);
    const canSave = !busy && (exempt ? parts.length > 0 && numberOk : !!saved); // removing needs something saved
    const save = async () => {
        setBusy(true); setError(''); setNotice('');
        try {
            await setCustomerTaxExemptionCall({ customerId: customer.id, parts, number: parts.length ? number : '' });
            setNotice(parts.length ? 'Exemption saved. New estimates, jobs and invoices for this customer follow it.' : 'Exemption removed.');
        } catch (e) {
            setError(callErrorText(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border border-gray-200 rounded-xl p-4 bg-white text-sm space-y-3" data-testid="customer-tax-exemption">
            <div className="flex justify-between items-center"><span className="font-bold text-gray-900">Tax exemption</span><span className="text-xs text-gray-500">Optional</span></div>
            <label className="flex gap-2 items-center min-h-[44px]">
                <input type="checkbox" className="w-5 h-5" checked={exempt} onChange={(e) => setExempt(e.target.checked)} />
                <span>This customer is exempt from some or all tax</span>
            </label>
            {exempt && (
                <div className="pl-7 space-y-3">
                    <fieldset className="space-y-1">
                        <legend className="text-xs font-semibold text-gray-700">Exempt from</legend>
                        {choices.length === 0 && <p className="text-xs text-gray-500">Your company has no tax parts yet. Add them in Settings › Taxes.</p>}
                        {choices.map((c) => {
                            const k = normTaxPartName(c.name);
                            return (
                                <label key={k} className="flex gap-2 items-center min-h-[40px]">
                                    <input type="checkbox" className="w-5 h-5" checked={ticked.has(k)}
                                        onChange={(e) => setTicked((prev) => { const n = new Set(prev); if (e.target.checked) n.add(k); else n.delete(k); return n; })} />
                                    <span>{c.label}</span>
                                </label>
                            );
                        })}
                        <p className="text-xs text-gray-500">{qbConnected ? 'These are your company’s tax parts from QuickBooks.' : 'These are your company’s tax parts from Settings › Taxes.'}</p>
                    </fieldset>
                    <div className="space-y-1">
                        <label htmlFor={`${idPrefix}-number`} className="text-xs font-semibold text-gray-700">Exemption number <span className="text-red-700">· Required</span></label>
                        <input id={`${idPrefix}-number`} value={number} maxLength={EXEMPTION_NUMBER_MAX} onChange={(e) => setNumber(e.target.value)}
                            placeholder="e.g. status card or exemption certificate number" className="w-full min-h-[44px] px-3 border border-gray-300 rounded-lg" />
                        <p className="text-xs text-gray-500">Printed on the customer’s documents beside the tax that wasn’t charged.</p>
                    </div>
                </div>
            )}
            {error && <p className="text-xs text-red-700" role="alert">{error}</p>}
            {notice && <p className="text-xs text-gray-600" role="status">{notice}</p>}
            <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                <span className="text-xs text-gray-500">Owner, Admin, Manager and Office Staff can change this.</span>
                <button type="button" onClick={save} disabled={!canSave}
                    className="min-h-[40px] px-3 rounded-lg font-semibold text-white bg-[#5F4EC9] disabled:bg-[#a5a0d6]">Save exemption</button>
            </div>
        </div>
    );
}
